<script setup lang="ts">
import { computed } from 'vue'
import { ECardSizeDesktop, ECardSizeMobile, ECardsListLayout, ESeoHeadingSize, type ICardIconProps } from '../ts/types'
import { scrollTo } from '../../../utils/scrollTo'
import { useImage } from '../../../composables/useImageUrl'
import { getLinkTarget } from '../../../utils/linkTarget'
import { NuxtLink } from '#components'
import { isDark } from '@/ui/helpers/utils'
import VCarouselSlide from '@/ui/plushComponents/VCarousel/VCarouselSlide.vue'

const props = withDefaults(defineProps<ICardIconProps>(), {
    seoHeadingSize: ESeoHeadingSize.H2,
})

const { image: responsiveImage } = useImage(props.image, props.page)

const mobileCardSize =
    props.mobileLayout === ECardsListLayout.Grid
        ? {
              [ECardSizeMobile.Small]:
                  'max-md:!basis-[calc(50%-0.25rem)] max-md:!max-w-full md:max-lg:!basis-[calc(25%-0.75rem)] md:max-lg:!max-w-full',
              [ECardSizeMobile.Large]:
                  'max-md:!basis-full md:max-lg:!basis-[calc(50%-0.5rem)] max-md:!max-w-full md:max-lg:!max-w-full',
          }
        : {}

const desktopCardSize =
    props.mobileLayout === ECardsListLayout.Grid
        ? {
              [ECardSizeDesktop.Small]: 'lg:!basis-[calc(25%-1.125rem)]',
              [ECardSizeDesktop.Large]: 'lg:!basis-[calc(33.3%-1rem)]',
              [ECardSizeDesktop.Fluid]: '',
          }
        : {}

const component = computed(() => {
    if (props.action?.url) {
        return 'a'
    } else if (props.action) {
        return 'button'
    } else {
        return 'div'
    }
})
</script>

<template>
    <VCarouselSlide
        class="group"
        :class="[
            mobileCardSize[mobileSize],
            desktopCardSize[desktopSize],
            { 'max-lg:!px-0': mobileLayout === ECardsListLayout.Grid },
            { 'lg:!px-0': desktopLayout === ECardsListLayout.Grid },
            { 'flex-1 !shrink !grow': desktopSize === ECardSizeDesktop.Fluid },
        ]"
    >
        <component
            :is="component"
            :href="action?.url"
            :data-test="dataTest"
            :target="action ? getLinkTarget(action) : undefined"
            class="py-base pr-xs gap-base relative block w-full overflow-hidden lg:text-center"
            :class="{
                'max-lg:!gap-x-sm max-lg:flex':
                    mobileSize === ECardSizeMobile.Large && mobileLayout === ECardsListLayout.Grid,
            }"
            @click="action?.anchor ? scrollTo(action?.anchor) : null"
        >
            <BaseImage
                class="size-[56px] lg:mx-auto lg:size-[88px]"
                picture-classes="!inline-block"
                :image-data="responsiveImage"
                :alt="image.altText"
            />
            <div>
                <component
                    :is="seoHeadingSize"
                    class="heading-2xs md:heading-xs lg:heading-sm text-comp-high-contrast line-clamp-3"
                    :class="[isDark(dark)]"
                >
                    {{ title }}
                </component>
                <div
                    v-html-gpp="paragraph"
                    class="paragraph-md text-neutral-primary mt-[2px] line-clamp-5"
                    :class="{ 'lg:mt-xs': desktopSize === ECardSizeDesktop.Large }"
                />
            </div>
        </component>
    </VCarouselSlide>
</template>
